import React from 'react'
import { videoStillURL } from 'components/ad-preview'
import { dark, radius, type as typo } from '../tokens'
import { MediaThumb } from './mediaDraft'

/**
 * The controls the three media editors share. Extracted because the same button
 * appears against a single ad's tile, a carousel's cards and a dynamic ad's
 * assets — not to unify the three editors, which show genuinely different things.
 */

/** A quiet text action, the treatment the pool editor's "Add" already uses. */
export function ActionButton({
  label,
  onClick,
  disabled,
  title,
  style,
}: {
  label: string
  onClick: () => void
  disabled?: boolean
  title?: string
  style?: React.CSSProperties
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        padding: 0,
        border: 'none',
        background: 'transparent',
        fontFamily: typo.family.sans,
        fontSize: typo.size.body,
        color: dark.accent,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        ...style,
      }}
    >
      {label}
    </button>
  )
}

/**
 * An icon-only control. `dark.textMuted` at minimum: `textFaint` is for text with
 * a label beside it, and this glyph carries the whole button.
 */
export function IconButton({
  icon,
  label,
  onClick,
  disabled,
  title,
}: {
  icon: string
  label: string
  onClick: () => void
  disabled?: boolean
  title?: string
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={title ?? label}
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 28,
        height: 28,
        flexShrink: 0,
        padding: 0,
        border: 'none',
        background: 'transparent',
        color: dark.textMuted,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {/* Font Awesome 5 with no v4 shim: a v4-only name renders no glyph at all. */}
      <i className={`fa fa-${icon}`} aria-hidden="true" />
    </button>
  )
}

/** A media thumbnail that can be picked. */
export function MediaTile({
  thumb,
  size,
  selected,
  label,
  onClick,
}: {
  thumb?: MediaThumb
  size: number
  selected: boolean
  label: string
  onClick: () => void
}) {
  const fill: React.CSSProperties = { width: '100%', height: '100%', objectFit: 'cover' }
  return (
    <button
      type="button"
      aria-pressed={selected}
      aria-label={label}
      onClick={onClick}
      style={{
        width: size,
        height: size,
        padding: 0,
        overflow: 'hidden',
        borderRadius: radius.sm,
        border: `2px solid ${selected ? dark.accent : dark.hairline}`,
        background: dark.surface,
        opacity: selected ? 1 : 0.65,
        cursor: 'pointer',
      }}
    >
      {thumb?.kind === 'image' && <img src={thumb.url} alt="" style={fill} />}
      {/* A frame of the video, standing in for a Facebook thumbnail that does
        not exist yet. Inert: the tile's whole job is to be picked. */}
      {thumb?.kind === 'video' && (
        <video
          src={videoStillURL(thumb.url)}
          muted
          playsInline
          preload="metadata"
          tabIndex={-1}
          style={{ ...fill, pointerEvents: 'none', background: '#000' }}
        />
      )}
    </button>
  )
}

/**
 * Said against a freshly uploaded video rather than blocking the save on it.
 * Playback in the preview is our stored copy, and the save only needs the
 * Facebook identifier the upload already persisted.
 */
export const PROCESSING_NOTE = 'You can save this video now. Facebook may still be processing it.'

/** The eyebrow every media section is headed with. */
export const mediaEyebrowStyle: React.CSSProperties = {
  fontFamily: typo.family.sans,
  fontSize: typo.size.eyebrow,
  fontWeight: typo.weight.semibold,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: dark.textFaint,
}
